import { OrderInfo, Shopper, ShopperSearchType } from 'common/types';
import { SearchType } from 'common/types';
import { getOrderNumberFromOrderBarcode } from 'common/modules/orderReceipts/utils';

const searchByName = (rental: OrderInfo, searchInput: string) => {
	const namesToSearch = getShopperNamesInLowerCase(rental);
	return namesToSearch.some((item) => includesString(item, searchInput));
};

const searchByProductCode = (rental: OrderInfo, searchInput: string) => {
	const productsToSearch = rental.rentalProductCodes;
	return productsToSearch.some((item) => isExactMatch(item, searchInput));
};

const searchByOrderBarcode = (rental: OrderInfo, searchInput: string) => {
	const orderNumber = getOrderNumberFromOrderBarcode(searchInput.toLocaleUpperCase());
	return !!orderNumber && rental.orderNumber === orderNumber;
};

const searchByConfirmationNumber = (rental: OrderInfo, searchInput: string) => {
	const confirmationNumber = rental.skidataProps?.confirmationNumber ?? '';
	return includesString(confirmationNumber, searchInput);
};

export const getSearchResults = (
	searchInput: string,
	searchTarget: OrderInfo[],
	searchTypes: SearchType[],
) => {
	const searchResults: OrderInfo[] = searchTarget.reduce((accumulator: OrderInfo[], rental) => {
		const hasSearchResult = (searchType: SearchType) => {
			switch (searchType) {
				case 'NAME':
					return searchByName(rental, searchInput);
				case 'PRODUCT_CODE':
					return searchByProductCode(rental, searchInput);
				case 'CONFIRMATION_NUMBER':
					return searchByConfirmationNumber(rental, searchInput);
				case 'ORDER_BARCODE':
					return searchByOrderBarcode(rental, searchInput);
				default:
					return false;
			}
		};
		const orderFoundFromSearch = searchTypes.some(hasSearchResult);
		return orderFoundFromSearch ? [...accumulator, rental] : accumulator;
	}, []);
	return searchResults;
};

export const getShopperNamesInLowerCase = (rental: OrderInfo) => {
	const shopperNamesToBeSearched: string[] = rental.shopperNames.map((name) => name.toLowerCase());
	if (rental.responsiblePerson && rental.responsiblePerson.external) {
		const responsiblePerson = rental.responsiblePerson.person;
		const responsibleFullName = (
			responsiblePerson.firstName +
			' ' +
			responsiblePerson.lastName
		).toLowerCase();
		shopperNamesToBeSearched.push(responsibleFullName);
	}
	return shopperNamesToBeSearched;
};

export const includesString = (searchTarget: string, searchInput: string) => {
	const searchTargetLowerCase = searchTarget.toLowerCase();
	const searchInputLowerCase = searchInput.toLowerCase();
	const includes = searchTargetLowerCase.includes(searchInputLowerCase);
	return includes;
};

export const isExactMatch = (searchTarget: string, searchInput: string) => {
	const searchTargetLowerCase = searchTarget.toLowerCase();
	const searchInputLowerCase = searchInput.toLowerCase();
	const includes = searchTargetLowerCase === searchInputLowerCase;
	return includes;
};

export const searchByShopperName = (shopper: Shopper, searchValue: string) => {
	const fullName = `${shopper.firstName} ${shopper.lastName}`;
	return includesString(fullName, searchValue);
};

export const searchByShopperEmail = (shopper: Shopper, searchValue: string) => {
	const email = shopper.email;
	return includesString(email, searchValue);
};

export const searchByShopperPhone = (shopper: Shopper, searchValue: string) => {
	const phone = shopper.phone;
	return includesString(phone, searchValue);
};

export const getShopperSearchResults = (
	searchValue: string,
	shoppers: Shopper[],
	searchTypes: ShopperSearchType[],
) => {
	const searchResults: Shopper[] = shoppers.reduce((acc: Shopper[], shopper) => {
		const hasSearchResult = (searchType: ShopperSearchType) => {
			switch (searchType) {
				case 'NAME':
					return searchByShopperName(shopper, searchValue);
				case 'EMAIL':
					return searchByShopperEmail(shopper, searchValue);
				case 'PHONE':
					return searchByShopperPhone(shopper, searchValue);
				default:
					return false;
			}
		};

		const shoppersFoundFromSearch = searchTypes.some(hasSearchResult);

		return shoppersFoundFromSearch ? [...acc, shopper] : acc;
	}, []);
	return searchResults;
};

const removeEmojiCharacters = (text: string) => {
	return text
		.replace(
			/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
			'',
		)
		.replace(/\s+/g, ' ')
		.trim();
};

/**
 * Splits string to an array of @wordCount length elements
 * @param _text The string to split
 * @param wordCount The word count to split to, default to 3
 * @returns Splitted string array
 */
export const stringToSearchArray = (_text: string, wordCount: number = 3) => {
	const searchArray = [];
	const text = removeEmojiCharacters((_text || '').toLowerCase());
	if (text === '') return [];
	searchArray.push(text.substring(0, wordCount));
	for (let i = 1; i <= text.length - wordCount; i++) {
		searchArray.push(text.substring(0, i + wordCount));
	}
	return searchArray;
};
